@if (showVideoUploading) {
  <div class="d-flex flex-column justify-content-center align-items-center" style="min-height: 400px;">
    <div class="row text-center">
      @if (uploadInProgress) {
        <h5 class="text-info">
          Please wait while your video is being uploaded... <strong>Do not close this window.</strong>
        </h5>
      }
    </div>
    @if (uploadInProgress) {
      <div class="row text-center mt-3">
        <circle-progress
          class="flex"
          [percent]="progressValue"
          [radius]="100"
          [outerStrokeWidth]="6"
          [showInnerStroke]="false"
          [outerStrokeColor]="'#5b7ffc'"
          [animation]="false"
          [showSubtitle]="false"
          [titleColor]="'#5b7ffc'"
          [backgroundPadding]="20"
          [unitsColor]="'#5b7ffc'">
        </circle-progress>
      </div>
    }
    @if (!uploadInProgress) {
      <div class="row text-center mt-3">
        <h5 class="text-success" class="flex">Upload Complete! Redirecting...</h5>
      </div>
    }
  </div>
}

@if (!showVideoUploading) {
  <div class="row">
    <div class="col-lg-7">
      <div class="card">
        <div class="card-header">
          @if (!isRecording) {
            <h5 class="card-title mb-0">Step 1: Camera & Conditions Check</h5>
          }
          @if (isRecording) {
            <h5 class="card-title mb-0 text-danger">
              <i class="ri-record-circle-fill align-middle"></i> RECORDING...
            </h5>
          }
        </div>
        <div class="card-body">
          <div class="ratio ratio-16x9 bg-light rounded">
            <video class="rounded w-100 h-100" style="transform: scaleX(-1);"
            #videoPlayer playsinline autoplay [muted]="testSpeakers || muteMic"></video>
            @if (isRecording) {
              <div class="overlay-time-blinking">{{ time }}</div>
            }
          </div>
        </div>
        @if (!isRecording) {
          <div class="card-footer">
            <div class="d-flex gap-3">
              @if (streamService.detect_face) {
                <div class="text-success">
                  <i class="ri-check-double-line align-middle"></i> Face Detected
                </div>
              }
              @if (!streamService.detect_face) {
                <div class="text-danger">
                  <i class="ri-error-warning-line align-middle"></i> Face Not Detected
                </div>
              }
              @if (streamService.light) {
                <div class="text-success">
                  <i class="ri-check-double-line align-middle"></i> Good Lighting
                </div>
              }
              @if (!streamService.light) {
                <div class="text-danger">
                  <i class="ri-error-warning-line align-middle"></i> Poor Lighting
                </div>
              }
            </div>
          </div>
        }
      </div>
    </div>
    <div class="col-lg-5">
      @if (!isRecording) {
        <div class="card">
          <div class="card-header">
            <h5 class="card-title mb-0">Step 2: Settings & Guidelines</h5>
          </div>
          <div class="card-body">
            <form [formGroup]="setupForm">
              <div class="mb-3">
                <label for="speaker" class="form-label">Number of Speakers <span class="text-danger">*</span></label>
                <ng-select id="speaker" [items]="speakers" bindLabel="name" bindValue="value"
                  formControlName="speakerSelected" placeholder="Choose number of speakers"
                  [ngClass]="{ 'is-invalid': f['speakerSelected'].touched && f['speakerSelected'].errors }">
                </ng-select>
              </div>
              <div class="mb-3">
                <label for="camera" class="form-label">Camera <span class="text-danger">*</span></label>
                <ng-select formControlName="selectedCamera" [items]="videoDevices" bindLabel="label"
                  bindValue="deviceId" (change)="onCameraChange()"
                  [ngClass]="{ 'is-invalid': f['selectedCamera'].touched && f['selectedCamera'].errors }">
                </ng-select>
              </div>
              <div class="mb-3">
                <label for="mic" class="form-label">Microphone <span class="text-danger">*</span></label>
                <ng-select formControlName="selectedMicrophone" [items]="audioInputDevices" bindLabel="label"
                  bindValue="deviceId" (change)="onMicrophoneChange()"
                  [ngClass]="{ 'is-invalid': f['selectedMicrophone'].touched && f['selectedMicrophone'].errors }">
                </ng-select>
                <div class="pids-wrapper w-100 mt-2 d-flex">
                  <div class="pid rounded m-1"></div>
                  <div class="pid rounded m-1"></div>
                </div>
              </div>
              <div class="mb-3">
                <label for="speakers" class="form-label">Speakers <span class="text-danger">*</span></label>
                <div class="input-group">
                  <ng-select class="form-control" formControlName="selectedSpeaker" [items]="audioOutputDevices"
                    bindLabel="label" bindValue="deviceId" [clearable]="false"
                    [ngClass]="{ 'is-invalid': f['selectedSpeaker'].touched && f['selectedSpeaker'].errors }">
                  </ng-select>
                  <button class="btn btn-primary" type="button" (click)="playsound()" title="Test Speaker">
                    <i class="ri-volume-up-line align-bottom"></i>
                  </button>
                </div>
                <audio #audioPlayer></audio>
              </div>
              <div class="alert alert-info bg-info-subtle text-info border-0">
                <strong>Guidelines:</strong> Please ensure you are in a quiet, well-lit room. Look directly at the camera.
              </div>
            </form>
          </div>
          <div class="card-footer">
            <button type="button" (click)="start()" class="btn btn-primary btn-label w-100"
              [disabled]="!allowStartRecording || setupForm.invalid">
              <i class="ri-play-circle-line label-icon align-middle fs-16 me-2"></i>
              Start Recording
            </button>
          </div>
        </div>
      }
      @if (isRecording) {
        <div class="card">
          <div class="card-header">
            <h5 class="card-title mb-0">Recording Controls</h5>
          </div>
          <div class="card-body">
            <p class="text-muted">
              Your session is now being recorded. Please speak clearly.
            </p>
            @if (f['speakerSelected'].value === 'n') {
              <div>
                <label class="form-label">Voice Sample Baseline</label>
                <div class="d-flex gap-2 mb-3">
                  <button type="button" (click)="markTime('v_start')" class="btn w-100"
                    [ngClass]="!isMarked('v_start') ? 'btn-soft-secondary' : 'btn-success'"
                    [disabled]="isMarked('v_start')">
                    <i class="ri-volume-down-line align-middle"></i> Mark Start
                  </button>
                  <button type="button" (click)="markTime('v_end')" class="btn w-100"
                    [ngClass]="!isMarked('v_end') ? 'btn-soft-secondary' : 'btn-success'"
                    [disabled]="isMarked('v_end')">
                    <i class="ri-volume-up-line align-middle"></i> Mark End
                  </button>
                </div>
                <button type="button" (click)="markTime('reset_v')" class="btn btn-soft-danger w-100 mb-3">
                  <i class="ri-delete-back-2-line align-middle"></i> Reset Baseline
                </button>
              </div>
            }
            <button type="button" (click)="stopRecording()" class="btn btn-danger btn-label w-100">
              <i class="ri-stop-circle-line label-icon align-middle fs-16 me-2"></i>
              Stop Recording
            </button>
          </div>
        </div>
      }
    </div>
  </div>
}

<ng-template #voiceBaseModal let-modal>
  <div class="modal-header">
    <h5 class="modal-title">Guidelines for Voice Sample</h5>
    <button type="button" class="btn-close" (click)="modal.dismiss()"></button>
  </div>
  <div class="modal-body">
    <p>You have selected "Multiple Speakers". Before the main analysis, please provide a voice sample of the **primary subject** speaking alone for 10-15 seconds.</p>
    <p>1. Click "Start Recording".</p>
    <p>2. Have the primary subject speak.</p>
    <p>3. Click "Mark Start" when they begin.</p>
    <p>4. Click "Mark End" when they finish.</p>
    <p>5. Proceed with the rest of your conversation.</p>
  </div>
  <div class="modal-footer">
    <button type="button" class="btn btn-light" (click)="modal.dismiss()">Cancel</button>
    <button type="button" class="btn btn-primary" (click)="startRecording()">Start Recording</button>
  </div>
</ng-template>

<ng-template #startRecordingModal let-modal>
  <div class="modal-header">
    <h5 class="modal-title">Guidelines for Baseline Calibration</h5>
    <button type="button" class="btn-close" (click)="modal.dismiss('Cross click')"></button>
  </div>
  <div class="modal-body">
    <p>Before the session begins, the system needs to calibrate its analysis models.</p>
    <p>Please have the **primary subject** (the person being assessed) introduce themselves and speak naturally for about **30 seconds** at the beginning of the recording.</p>
    <p>This calibration period is essential for accurate results. After 30 seconds, you can proceed with the rest of your conversation normally.</p>
  </div>
  <div class="modal-footer">
    <button type="button" class="btn btn-light" (click)="modal.dismiss('Close click')">Cancel</button>
    <button type="button" class="btn btn-primary" (click)="continueAfterGuidelines()">Continue</button>
  </div>
</ng-template>